import React from 'react';
import {
  DocPage, PageHeader, Section, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './BillableMetric.css';

const TOC = [
  { id: 'standard-flow', label: 'The default transformation'           },
  { id: 'solution',      label: 'Replacing the default transformation' },
  { id: 'problem',       label: 'Scenarios'                            },
  { id: 'combining',     label: 'Combining multiple metrics'           },
];


// ── Events table ──────────────────────────────────────────────────────────────

function EventsTable({ eventCols, events }) {
  return (
    <div className="bm-events-wrap">
      <table className="bm-events-table">
        <thead>
          <tr>
            {eventCols.map((col) => (
              <th key={col.key} className={`bm-th bm-th--${col.type}`}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((row, i) => (
            <tr key={i}>
              {eventCols.map((col) => (
                <td key={col.key} className={`bm-td bm-td--${col.type}`}>
                  {row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Standard-flow walkthrough ─────────────────────────────────────────────────

function CurrentFlow({ flow }) {
  return (
    <div className="bm-flow">
      <div className="bm-flow-step">
        <div className="bm-flow-step-num">1</div>
        <div className="bm-flow-step-body">
          <div className="bm-flow-step-title">Pick the meter</div>
          <div className="bm-flow-step-desc">
            The <strong>{flow.feeComponent}</strong> component reads one meter:{' '}
            <span className="bm-flow-pill">{flow.usageAttribute}</span>. Every event's value
            for this attribute enters the flow. All other columns are ignored.
          </div>
        </div>
      </div>

      <div className="bm-flow-step">
        <div className="bm-flow-step-num">2</div>
        <div className="bm-flow-step-body">
          <div className="bm-flow-step-title">Deduplicate by reference ID</div>
          <div className="bm-flow-step-desc">
            Each usage event carries a reference ID — a unique identifier you assign (e.g., an order
            ID or transaction ID). If you send the same ref ID more than once, the latest value wins.
            Events with the same ref ID are collapsed into one by summing the meter.
          </div>
          <div className="bm-flow-groups">
            {flow.groups.map((g) => (
              <div key={g.refId} className="bm-flow-group">
                <span className="bm-flow-group-ref">{g.refId}</span>
                <span className="bm-flow-group-calc">{g.calc}</span>
                <span className="bm-flow-group-arrow">=</span>
                <span className="bm-flow-group-total">{g.total}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bm-flow-step">
        <div className="bm-flow-step-num">3</div>
        <div className="bm-flow-step-body">
          <div className="bm-flow-step-title">Sum across groups</div>
          <div className="bm-flow-step-desc">
            Every group's total is added together into a single number — the quantity sent to
            the rate card.
          </div>
          <div className="bm-flow-sum">
            {flow.groups.map((g) => g.total).join(' + ')}
            <span className="bm-flow-sum-eq"> = </span>
            <span className="bm-flow-sum-total">{flow.finalSum}</span>
            <span className="bm-flow-sum-unit"> {flow.unit}</span>
          </div>
        </div>
      </div>

      <div className="bm-flow-step">
        <div className="bm-flow-step-num">4</div>
        <div className="bm-flow-step-body">
          <div className="bm-flow-step-title">Pipe to the rate card</div>
          <div className="bm-flow-step-desc">
            The rate card receives that single number and produces a charge.
          </div>
          <div className="bm-flow-ratecard">
            <div className="bm-flow-ratecard-label">{flow.rateCardLabel}</div>
            <div className="bm-flow-ratecard-calc">{flow.rateCardCalc}</div>
          </div>
          <div className="bm-flow-result">
            <span className="bm-flow-result-label">Invoice line</span>
            <span className="bm-flow-result-name">{flow.invoiceLine}</span>
            <span className="bm-flow-result-charge">{flow.charge}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Metric definition fields ──────────────────────────────────────────────────

function MetricFields({ filter, expression, aggregation }) {
  const steps = [
    { num: 1, key: 'Filter',      val: filter,      desc: 'keep events that match' },
    { num: 2, key: 'Expression',  val: expression,  desc: 'value per event' },
  ];
  if (aggregation) steps.push({ num: 3, key: 'Aggregation', val: aggregation, desc: 'combine into one number' });

  return (
    <div className="bm-metric-pipe">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <span className="bm-metric-pipe-arrow">→</span>}
          <div className="bm-metric-pipe-step">
            <div className="bm-metric-pipe-head">
              <span className="bm-metric-pipe-num">{s.num}</span>
              <span className="bm-metric-pipe-key">{s.key}</span>
            </div>
            <div className="bm-metric-pipe-val">{s.val}</div>
            <div className="bm-metric-pipe-desc">{s.desc}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Scenario card ─────────────────────────────────────────────────────────────

function ScenarioCard({ num, title, desc, children, output }) {
  return (
    <details className="bm-scenario-card" name="scenarios">
      <summary className="bm-scenario-card-summary">
        <span className="bm-scenario-card-num">{num}</span>
        <div className="bm-scenario-card-header-text">
          <span className="bm-scenario-card-title">{title}</span>
          <span className="bm-scenario-card-desc">{desc}</span>
        </div>
        <span className="bm-scenario-card-caret">▸</span>
      </summary>
      <div className="bm-scenario-card-body">
        {children}
        {output && <div className="bm-scenario-card-output">{output}</div>}
      </div>
    </details>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BillableMetric({ onNavigate }) {
  const { industry } = useIndustry();
  const bm = industry.billableMetric;
  const description = `A Billable Metric is a custom rule for turning usage events into a single number for pricing — letting you filter which events count, transform their values, and choose how they aggregate.`;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Products"
        title="Billable Metrics"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'meter',   label: 'Meters'   },
          { id: 'product-attribute', label: 'Product Attributes' },
          { id: 'fee',     label: 'Fees'     },
          { id: 'rate-card',         label: 'Rate Cards'         },
        ]}
      />

      <Callout type="info">
        <strong>A fee is driven by either a meter or billable metrics — not both.</strong>{' '}
        Once a billable metric is attached to a fee, the default meter-based aggregation no
        longer applies. The two modes are mutually exclusive per fee.
      </Callout>

      <Section id="standard-flow" title="The default transformation">
        <Para>
          Every fee needs one value to hand to its rate card. That value comes from a
          transformation the billing engine applies to the usage events in the billing period. By
          default the transformation is fixed: <strong>sum one meter across all events</strong>.
          Here are the events we'll apply that transformation to — meter columns
          in <span style={{ color: 'var(--teal-600)', fontWeight: 600 }}>teal</span>,
          product attribute columns
          in <span style={{ color: 'var(--purple-600)', fontWeight: 600 }}>purple</span>:
        </Para>
        <EventsTable eventCols={bm.eventCols} events={bm.events} />
        <Para>
          For the <strong>{bm.currentFlow.feeComponent}</strong> component, the default
          transformation runs these four steps and the rate card turns the resulting value into a fee:
        </Para>
        <CurrentFlow flow={bm.currentFlow} />
        <Callout type="info" title="What this default transformation cannot express">
          {bm.currentFlow.transitionNote}
        </Callout>
      </Section>

      <Section id="solution" title="Replacing the default transformation">
        <Para>
          A <strong>billable metric</strong> is a named transformation you define to replace the
          default one. It is applied to the same usage events and produces the value the rate card
          turns into a fee. You configure it with three parts:
        </Para>
        <div className="bm-components">
          <div className="bm-component">
            <div>
              <div className="bm-component-name">Filter</div>
              <span className="bm-component-optional">optional</span>
            </div>
            <div className="bm-component-body">
              Conditions on product attribute values or meter values.
              Only events that satisfy all conditions are included.
              <div className="bm-component-examples">
                {bm.filterExamples.map((ex) => (
                  <span key={ex} className="bm-component-example">{ex}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="bm-component">
            <div>
              <div className="bm-component-name">Expression</div>
              <span className="bm-component-optional">required</span>
            </div>
            <div className="bm-component-body">
              A formula applied per event before aggregation. Can reference any{' '}
              <strong>meter value</strong> or <strong>product attribute</strong> on the
              event, combined with arithmetic operators{' '}
              <span className="bm-component-example" style={{ display: 'inline' }}>+ - * /</span>.
              <div className="bm-component-examples">
                {bm.expressionExamples.map((ex) => (
                  <span key={ex} className="bm-component-example">{ex}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="bm-component">
            <div>
              <div className="bm-component-name">Aggregation</div>
              <span className="bm-component-optional">required</span>
            </div>
            <div className="bm-component-body">
              How to combine the per-event values into the single number sent to the rate card.
              <div className="bm-aggregation-options">
                <div className="bm-agg-row"><span className="bm-component-example">Sum</span><span>Total of all per-event values — total consumption across the period</span></div>
                <div className="bm-agg-row"><span className="bm-component-example">Count</span><span>Number of qualifying events — expression value is ignored</span></div>
                <div className="bm-agg-row"><span className="bm-component-example">Max</span><span>Highest single per-event value in the period</span></div>
                <div className="bm-agg-row"><span className="bm-component-example">Average</span><span>Mean of all per-event values</span></div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="problem" title="Scenarios">
        <Para>
          Four requirements the default sum cannot express — each shown using the events above.
        </Para>
        <details className="bm-events-ref">
          <summary className="bm-events-ref-summary">View events table ▸</summary>
          <EventsTable eventCols={bm.eventCols} events={bm.events} />
        </details>
        <div className="bm-scenario-list">

          <ScenarioCard
            num={1}
            title={bm.problems[0].title}
            desc={bm.problems[0].desc}
            output={bm.filterScenario.after}
          >
            <MetricFields
              filter={bm.filterScenario.filter}
              expression={bm.filterScenario.expression}
              aggregation={bm.filterScenario.aggregation}
            />
          </ScenarioCard>

          <ScenarioCard
            num={2}
            title={bm.problems[1].title}
            desc={bm.problems[1].desc}
            output={bm.transformScenario.after}
          >
            <MetricFields
              filter={bm.transformScenario.filter}
              expression={bm.transformScenario.expression}
              aggregation={bm.transformScenario.aggregation}
            />
          </ScenarioCard>

          <ScenarioCard
            num={3}
            title={bm.problems[2].title}
            desc={bm.problems[2].desc}
          >
            <div className="bm-multi-metrics">
              {bm.conditionalScenario.metrics.map((m) => (
                <div key={m.name} className="bm-multi-metric">
                  <div className="bm-multi-metric-header">
                    <span className="bm-multi-metric-name">{m.name}</span>
                    <span className="bm-multi-metric-result">→ {m.result}</span>
                  </div>
                  <div className="bm-multi-metric-fields">
                    <MetricFields filter={m.filter} expression={m.expression} aggregation={m.aggregation} />
                  </div>
                </div>
              ))}
            </div>
          </ScenarioCard>

          <ScenarioCard
            num={4}
            title={bm.problems[3].title}
            desc={bm.problems[3].desc}
            output={bm.thresholdScenario.after}
          >
            <MetricFields
              filter={bm.thresholdScenario.filter}
              expression={bm.thresholdScenario.expression}
              aggregation={bm.thresholdScenario.aggregation}
            />
            <Callout type="tip">
              A zero-rate first tier produces the same total but the invoice count includes
              events that were never charged — customers see three bags, not two. The filter
              eliminates this.
            </Callout>
          </ScenarioCard>

        </div>
      </Section>

      <Section id="combining" title="Combining multiple metrics on one fee">
        <Para>
          A fee can have more than one billable metric. Each runs independently — applying
          its own filter, expression, and aggregation to the full set of events for the
          billing period. The results are summed and passed to the rate card as a single
          number, producing one invoice line.
        </Para>
        <Para>
          This is the right pattern when a fee has fundamentally different billing rules
          for different event types. Rather than encoding complex branching logic into one
          metric, define one metric per case and let the system combine them.
        </Para>
        <Para>
          The Scenarios section above includes an example: two metrics on{' '}
          {bm.conditionalScenario.fee} — one per event type, each with its own filter and
          expression. At period close, the engine sums their results into a single value
          before passing it to the rate card.
        </Para>
        <Callout type="info">
          The rate card receives one combined number — not two separate inputs. The
          combination happens before pricing runs, so tier thresholds and volume
          calculations evaluate against the pooled total.
        </Callout>
      </Section>

    </DocPage>
  );
}
